import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { withTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import * as UIcons from '@iconscout/react-unicons'
import Btn, { FormBtn } from 'components/Btn'
import Input from 'components/Input'
import PhoneNumberInput from 'components/PhoneNumberInput'
import { apiRequest } from 'store/Misc/apiRequest'
import { setFormLoading } from 'store/slices/miscSlice'
import { ContactValidation } from 'pages/Settings/validation'

export default withTranslation()(function ContactForm({ t, contact, handleClick, onSaved }) {
  const dispatch = useDispatch()

  const method = useForm({ mode: 'all', resolver: yupResolver(ContactValidation) })
  const { reset, handleSubmit, formState: { isValid, isDirty } } = method

  useEffect(() => {
    if (contact) {
      reset({
        first_name: contact.first_name,
        last_name: contact.last_name,
        email: contact.email,
        job_title: contact.job_title,
        contact_type: contact.contact_type,
        phone: contact.phone
      })
    }
  }, [contact, reset])

  const typeOptions = [
    { label: t('settings.business_organization.contact_person.types.primary'), value: 'primary' },
    { label: t('settings.business_organization.contact_person.types.general'), value: 'general' },
    { label: t('settings.business_organization.contact_person.types.feedback'), value: 'feedback' }
  ]

  const submit = async (payload) => {
    dispatch(setFormLoading())
    try {
      await apiRequest({
        method: contact ? 'put' : 'post',
        url: contact ? `merchants/contacts/${contact.id}` : 'merchants/contacts',
        payload
      })

      toast.success(t(contact ? 'settings.business_organization.contact_person.success_update' : 'settings.business_organization.contact_person.success_create'))
      onSaved && onSaved()
      handleClick()
    } catch ({ message }) {
      toast.error(message)
    } finally {
      dispatch(setFormLoading('close'))
    }
  }

  return (
    <div className="fixed inset-0 z-[5000] bg-black bg-opacity-30 flex items-center justify-center p-4" onClick={handleClick}>
      <div className="bg-white relative p-6 md:p-8 w-11/12 max-w-md max-h-[90vh] overflow-y-auto rounded-xl" onClick={(event) => event.stopPropagation()}>
        <div className="relative flex items-center justify-center mb-6">
          <Btn
            className="w-10 h-10 hover:bg-default absolute left-0"
            content={() => <UIcons.UilTimes className="text-secondary" />}
            click={handleClick}
          />

          <h3 className="text-xl font-bold text-dark-blue">
            {t(contact ? 'settings.business_organization.contact_person.edit_title' : 'settings.business_organization.contact_person.add_title')}
          </h3>
        </div>

        <FormProvider {...method}>
          <form onSubmit={handleSubmit(submit)}>
            <div className="grid md:grid-cols-2 gap-5 mb-3">
              <Input
                name="first_name"
                label={t('settings.business_organization.contact_person.form.first_name.label')}
                placeholder={t('settings.business_organization.contact_person.form.first_name.placeholder')}
                variant="outlined"
              />

              <Input
                name="last_name"
                label={t('settings.business_organization.contact_person.form.last_name.label')}
                placeholder={t('settings.business_organization.contact_person.form.last_name.placeholder')}
                variant="outlined"
              />
            </div>

            <div className="mb-3">
              <Input
                name="email"
                label={t('settings.business_organization.contact_person.form.email.label')}
                placeholder={t('settings.business_organization.contact_person.form.email.placeholder')}
                variant="outlined"
              />
            </div>

            <div className="mb-3">
              <Input
                name="job_title"
                label={t('settings.business_organization.contact_person.form.position.label')}
                placeholder={t('settings.business_organization.contact_person.form.position.placeholder')}
                variant="outlined"
              />
            </div>

            <div className="mb-3">
              <Input
                name="contact_type"
                label={t('settings.business_organization.contact_person.form.contact_type.label')}
                placeholder={t('settings.business_organization.contact_person.form.contact_type.placeholder')}
                variant="outlined"
                options={typeOptions}
              />
            </div>

            <div className="mb-6">
              <PhoneNumberInput
                name="phone"
                label={t('settings.business_organization.contact_person.form.phone.label')}
                className={{}}
                singleInputClassName="w-full bg-white rounded-lg py-3"
                country="NG"
                code="234"
              />
            </div>

            <FormBtn
              content={t(contact ? 'misc.save' : 'settings.business_organization.contact_person.add_title')}
              className="w-full h-12 rounded-xl bg-primary text-white disabled:opacity-40"
              disabled={!isValid || !isDirty}
            />
          </form>
        </FormProvider>
      </div>
    </div>
  )
})
